import { clsx } from '@alexis/helpers/clsx';
import { gtmTrack } from '@alexis/helpers/gtm';
import { appendSearchParams } from '@alexis/helpers/searchParam';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { CLOUDINARY_DAILY_KEY } from '@constants/cloudinary';
import { GTM_ID } from '@constants/general';

import { useFlightContext } from '@context/FlightContext';

import { getPreCheckedStatusForFlightComparisonProviders } from '@helpers/flight';
import { getImageTransformationsUrl } from '@helpers/imageTransformations';

import OpenInNewIcon from '@icons/open_in_new.svg';

import { useGetFlightComparisonProviders } from '@hooks/api/flights/useGetFlightComparisonProviders';

import { getCurrencyState, getTranslationsState } from '@redux/selectors';

import styles from './FlightResultRHS.module.scss';

interface FlightResultRHSProps {
  isShowingMrecOrTopStickyAds: boolean;
  gtmAuth: string;
  gtmPreview: string;
  wegoAnalyticsClientSessionId: string | undefined;
  wegoAnalyticsClientId: string | undefined;
}

const FlightResultRHS: React.FC<FlightResultRHSProps> = ({
  isShowingMrecOrTopStickyAds,
  gtmAuth,
  gtmPreview,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
}) => {
  const [searchParams] = useSearchParams();
  const { flightSearchFormHeight } = useFlightContext();
  const currencyCode = useSelector(getCurrencyState).code;
  const translations = useSelector(getTranslationsState);

  const { isMultiCity, adultCount, childCount, cabinClass, infantCount, legs, isRoundTrip } =
    useFlightSearchPageParams();

  const { search } = useFlightSearchResults();

  const flightMetaSearchLegs = search?.legs;

  const firstLegOutboundDate = legs[0].outboundDate;
  const secondLegOutboundDate = isRoundTrip && legs[1] ? legs[1].outboundDate : undefined;

  const bowOnlyParam = searchParams.get('bow_only');

  const [flightComparisonProviders, setFlightComparisonProviders] = useState<
    FlightComparisonProvider[]
  >([]);

  const [compareImageHasError, setCompareImageHasError] = useState<boolean>(false);

  const getCompareAdHandoffUrl = useCallback(
    (handoffUrl: string) => {
      const searchParams = {
        currency_code: currencyCode,
        cabin: cabinClass,
        outbound_date: firstLegOutboundDate,
        ...(secondLegOutboundDate && { inbound_date: secondLegOutboundDate }),
        adults_count: adultCount,
        ...(childCount > 0 && { children_count: childCount }),
        ...(infantCount > 0 && { infants_count: infantCount }),
        wc: wegoAnalyticsClientId,
        ws: wegoAnalyticsClientSessionId,
      };

      return appendSearchParams(handoffUrl, searchParams);
    },
    [
      adultCount,
      cabinClass,
      childCount,
      currencyCode,
      firstLegOutboundDate,
      infantCount,
      secondLegOutboundDate,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
    ],
  );

  const gtmTrackCompareUnits = useCallback(() => {
    const gtmData = {
      event: 'eventTracked',
      eventAction: 'compare-units',
      eventCategory: 'flights',
      eventLabel: 'leaderboard',
    };
    gtmTrack(GTM_ID, gtmAuth, gtmPreview, gtmData);
  }, [gtmAuth, gtmPreview]);

  // Get flight comparison providers
  useGetFlightComparisonProviders(
    {
      departureAirportCode: flightMetaSearchLegs?.[0]?.departureAirport?.code,
      departureCityCode: flightMetaSearchLegs?.[0]?.departureCity.code ?? '',
      departureCountryCode: flightMetaSearchLegs?.[0]?.departureCity.countryCode ?? '',
      departureDate: flightMetaSearchLegs?.[0]?.outboundDate ?? '',
      arrivalAirportCode: flightMetaSearchLegs?.[0]?.arrivalAirport?.code,
      arrivalCityCode: flightMetaSearchLegs?.[0]?.arrivalCity.code ?? '',
      arrivalCountryCode: flightMetaSearchLegs?.[0]?.arrivalCity.countryCode ?? '',
      returnDate: flightMetaSearchLegs?.[1]?.outboundDate ?? '',
      placementType: 'leaderboard',
    },
    {
      enabled: Boolean(!isMultiCity && flightMetaSearchLegs?.[0] && !bowOnlyParam),
      onSuccessCallback: (flightComparisonProviders) => {
        getPreCheckedStatusForFlightComparisonProviders(flightComparisonProviders);

        setFlightComparisonProviders(flightComparisonProviders);
      },
      onErrorCallback: () => {
        setFlightComparisonProviders([]);
      },
    },
  );

  return (
    <>
      <div className={clsx(styles.adsContainer, isShowingMrecOrTopStickyAds && styles.show)}>
        <div id='jwp_container'></div>
        <div className={styles.mrec1} id='mrec_1'></div>
        <div id='mrec_2'></div>

        {flightComparisonProviders && flightComparisonProviders.length > 0 && (
          <div className={styles.compareSite}>
            <div className={styles.label}>{translations.compare_websites}</div>

            {flightComparisonProviders.map((flightComparisonProvider) => (
              <a
                href={getCompareAdHandoffUrl(flightComparisonProvider.handoffUrl)}
                rel='noreferrer'
                target='_blank'
                key={flightComparisonProvider.provider.code}
                className={styles.flightComparisonProvider}
                onClick={gtmTrackCompareUnits}
              >
                <img
                  className={clsx(styles.mainImage, compareImageHasError && styles.hide)}
                  onError={() => setCompareImageHasError(true)}
                  onLoad={() => setCompareImageHasError(false)}
                  src={
                    flightComparisonProvider.provider.providerType === 'ota'
                      ? getImageTransformationsUrl(
                          `https://assets.wego.com/image/upload/${CLOUDINARY_DAILY_KEY}/providers/rectangular_logos/${flightComparisonProvider.provider.code}.png`,
                        )
                      : getImageTransformationsUrl(
                          `https://assets.wego.com/image/upload/${CLOUDINARY_DAILY_KEY}/flights/airlines_rectangular/${flightComparisonProvider.provider.airlineCode}.png`,
                        )
                  }
                />
                <div className={styles.nameAndIcon}>
                  {flightComparisonProvider.provider.name}
                  <OpenInNewIcon className={styles.icon} />
                </div>
              </a>
            ))}
          </div>
        )}

        <div className={styles.stickyAdsContainer} style={{ top: `${flightSearchFormHeight}px` }}>
          <div id='sticky_top'></div>
          <div id='sticky_bottom'></div>
        </div>
      </div>

      <div id='skyscraper' className={styles.skyscraperAdsContainer}></div>
    </>
  );
};

export default FlightResultRHS;
